import { createTheme, Theme } from '@mui/material/styles';
import { ThemeMode } from './themeSlice';
import { lightPalette, darkPalette } from './palette';
import typography from './typography';
import shape from './shape';
import breakpoints from './breakpoints';
import { lightCustomShadows, darkCustomShadows } from './shadows';
import ComponentsOverrides from './overrides';

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    gradients: {
      primary: string;
      info: string;
      success: string;
      warning: string;
      error: string;
    };
    chart: {
      violet: string[];
      blue: string[];
      green: string[];
      yellow: string[];
      red: string[];
    };
  }

  interface PaletteOptions {
    tertiary?: {
      main: string;
      light?: string;
      dark?: string;
      contrastText?: string;
    };
    gradients?: {
      primary?: string;
      info?: string;
      success?: string;
      warning?: string;
      error?: string;
    };
    chart?: {
      violet?: string[];
      blue?: string[];
      green?: string[];
      yellow?: string[];
      red?: string[];
    };
  }

  interface ThemeOptions {
    customShadows?: {
      z1: string;
      z8: string;
      z12: string;
      z16: string;
      z20: string;
      z24: string;
      primary: string;
      secondary: string;
      info: string;
      success: string;
      warning: string;
      error: string;
    };
  }

  interface Theme {
    customShadows: {
      z1: string;
      z8: string;
      z12: string;
      z16: string;
      z20: string;
      z24: string;
      primary: string;
      secondary: string;
      info: string;
      success: string;
      warning: string;
      error: string;
    };
  }
}

const baseTheme = {
  typography,
  shape,
  breakpoints,
};

const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    ...lightPalette,
    mode: 'light',
    tertiary: {
      main: '#7c4dff',
      light: '#b47cff',
      dark: '#3f1dcb',
      contrastText: '#ffffff',
    },
  },
  // shadows: lightShadows, // Use default MUI shadows
  customShadows: lightCustomShadows,
});

const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    ...darkPalette,
    mode: 'dark',
    tertiary: {
      main: '#b39ddb',
      light: '#e6ceff',
      dark: '#836fa9',
      contrastText: '#000000',
    },
  },
  // shadows: darkShadows, // Use default MUI shadows
  customShadows: darkCustomShadows,
});

// Apply component overrides
lightTheme.components = ComponentsOverrides(lightTheme);
darkTheme.components = ComponentsOverrides(darkTheme);

export const getTheme = (mode: ThemeMode): Theme => {
  return mode === 'light' ? lightTheme : darkTheme;
};
